// @ts-nocheck
import React, { FC, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useForm, SubmitHandler } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { isEmpty } from 'lodash';
import { v4 as uuidv4 } from 'uuid';
import {
  LuPackage,
  LuHash,
  LuLink,
  LuScanLine,
  LuAppWindow,
  LuScan,
  LuInfo,
  LuPlus,
  LuPencil,
} from 'react-icons/lu';

import { Modal } from '@/components/elements/Modal';
import AppButton from '@/components/atoms/AppButton';
import ConfigInput from '@/components/elements/ConfigInput';
import ConfigDropdown from '@/components/elements/ConfigDropdown';

import { checkBarcodeValidationRule } from '@/constant/global/global.constant';
import { trimSpacesInPayload } from '@/utils/stringFormatting';
import { toggleHyphenation } from '@/utils/generic';
import { useDebounce } from '@/hooks/useDebounce';

import {
  getSubApplications,
  selectedSubApplication,
} from '@/store/slices/subApplication.slice';
import { subApplicationList } from '@/store/selectors/subApplication.selector';
import {
  createSmartCoolerProduct,
  updateSmartCoolerProduct,
} from '@/store/slices/smartCoolerProducts.slice';
import {
  getSmartCoolerProductsLoading,
  getSmartCoolerProductsError,
} from '@/store/selectors/smartCoolerProducts.selector';
import { APP_HEX } from '@/constant/theme/colors.constant';

import { tid } from '@/utils/testId';
import { SMART_COOLER_PRODUCTS_TEST_IDS as TID } from '@/constant/testIds/smartCoolerProducts.testIds';
import { Text } from '@/components/atoms/AppText';
import { CREATE_SMART_COOLER_PRODUCTS_PROPS } from './constant';
import type { ISmartCoolerSingleProductResponse } from './constant';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface ICrudCoolerProductModalProps {
  isOpen: boolean;
  onClose: (value: boolean) => void;
  /** Provide to enter Edit mode */
  product?: ISmartCoolerSingleProductResponse | null;
}

interface IFormInputs {
  shortProductName: string;
  productName?: string;
  legacyId?: string;
  barcodes: string;
  productImage?: string;
  smartCoolerProductId?: string;
}

// ─── Portal info-tooltip ────────────────────────────────────────────────────

const InfoTooltip = ({ info }: { info: string }) => {
  const iconRef = useRef<HTMLSpanElement>(null);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);

  const handleMouseEnter = () => {
    if (iconRef.current) {
      const r = iconRef.current.getBoundingClientRect();
      setPos({ top: r.top + r.height / 2, left: r.right + 8 });
    }
  };

  return (
    <>
      <span
        ref={iconRef}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={() => setPos(null)}
        className="flex-shrink-0 cursor-help"
      >
        <LuInfo className="w-3.5 h-3.5 text-blue-400 hover:text-blue-500 transition-colors" />
      </span>
      {pos &&
        createPortal(
          <div
            style={{
              position: 'fixed',
              top: pos.top,
              left: pos.left,
              transform: 'translateY(-50%)',
              zIndex: 999999,
              pointerEvents: 'none',
            }}
            className="w-48 rounded-lg bg-gray-900 dark:bg-gray-700 px-3 py-2 text-[11px] leading-snug text-white shadow-xl whitespace-normal"
          >
            {info}
            {/* Left-pointing caret */}
            <span
              style={{
                position: 'absolute',
                top: '50%',
                left: -6,
                transform: 'translateY(-50%)',
                borderWidth: 4,
                borderStyle: 'solid',
                borderColor: 'transparent',
                borderRightColor: APP_HEX.tooltip,
              }}
            />
          </div>,
          document.body,
        )}
    </>
  );
};

// ─── FieldLabel helper ───────────────────────────────────────────────────────

const FieldLabel = ({
  text,
  info,
  required,
}: {
  text: string;
  info: string;
  required?: boolean;
}) => (
  <span className="flex items-center justify-between w-full gap-1">
    <span className="flex items-center gap-1">
      {text}
      {required && (
        <Text as="span" size="base" color="danger" className="ml-0.5">
          *
        </Text>
      )}
    </span>
    <InfoTooltip info={info} />
  </span>
);

// ─── Component ────────────────────────────────────────────────────────────────

const CrudCoolerProductModal: FC<ICrudCoolerProductModalProps> = ({
  isOpen,
  onClose,
  product,
}) => {
  const dispatch = useDispatch();
  const isEditMode = !!product;

  const loading = useSelector(getSmartCoolerProductsLoading);
  const error = useSelector(getSmartCoolerProductsError);

  // Tracks whether THIS modal instance triggered the in-flight request
  const pendingRef = useRef(false);

  const [subAppId, setSubAppId] = useState('');
  const [isSubAppSelected, setIsSubAppSelected] = useState(false);
  const [searchApplication, setSearchApplication] = useState('');
  const [isHyphened, setIsHyphened] = useState(true);

  const searchApplicationQuery = useDebounce(searchApplication, 500);

  const subapplications = useSelector(subApplicationList).filter(
    (s) => s.name !== 'All SubApplications',
  );

  const { CREATE_SCP_FORM_PROPS } = CREATE_SMART_COOLER_PRODUCTS_PROPS;

  const {
    register,
    handleSubmit,
    reset,
    clearErrors,
    setValue,
    getValues,
    watch,
    trigger,
    formState: { errors, isValid, isSubmitting, isSubmitted, dirtyFields },
  } = useForm<IFormInputs>({ mode: 'onChange' });

  const smartProductIdValue = watch(
    CREATE_SCP_FORM_PROPS.SMART_PRODUCT_ID.REACT_HOOK_FORM_KEY_NAME,
  );

  // In edit mode: dirtyFields must be non-empty (something changed)
  const isLoading = pendingRef.current && loading;
  const isSaveDisabled =
    !isValid ||
    isLoading ||
    (!isEditMode && !isSubAppSelected) ||
    (isEditMode && isEmpty(dirtyFields));

  /* ── Auto-close on API response ─────────────────────────────────────── */
  useEffect(() => {
    if (pendingRef.current && !loading) {
      pendingRef.current = false;
      if (!error) {
        onClose(false);
      }
    }
  }, [loading]);

  /* ── Fetch sub-apps ──────────────────────────────────────────────────── */
  useEffect(() => {
    if (isOpen) {
      dispatch(
        getSubApplications({
          limit: 100,
          page: 1,
          keywords: searchApplicationQuery?.trim() ?? '',
        }),
      );
    }
  }, [isOpen, searchApplicationQuery]);

  /* ── Hydrate / reset on open ─────────────────────────────────────────── */
  useEffect(() => {
    if (!isOpen) {
      // Reset everything when closing
      pendingRef.current = false;
      reset();
      clearErrors();
      setSubAppId('');
      setIsSubAppSelected(false);
      setSearchApplication('');
      setIsHyphened(true);
      return;
    }

    if (isEditMode && product) {
      reset({
        shortProductName: product.shortProductName ?? '',
        productName: product.productName ?? '',
        legacyId: product.legacyId ?? '',
        barcodes: Array.isArray(product.barcodes)
          ? product.barcodes.join(',')
          : (product.barcodes ?? ''),
        productImage: product.productImage ?? '',
      });
    } else {
      // Create mode: pre-fill a UUID for the smart product ID
      reset();
      setValue(
        CREATE_SCP_FORM_PROPS.SMART_PRODUCT_ID.REACT_HOOK_FORM_KEY_NAME,
        uuidv4(),
        { shouldValidate: true },
      );
      setIsHyphened(true);
    }
  }, [isOpen, product]);

  /* ── Sub-application handler ─────────────────────────────────────────── */
  const handleSelectedSubApplication = (value: any) => {
    const id = value?.id ?? '';
    setSubAppId(id);
    setIsSubAppSelected(!isEmpty(id));
    if (!isEmpty(id)) dispatch(selectedSubApplication(value));
  };

  /* ── Submit ──────────────────────────────────────────────────────────── */
  const onSubmit: SubmitHandler<IFormInputs> = (formData) => {
    const barcodesArray = formData.barcodes
      ? formData.barcodes.split(',').map((s: string) => s.trim())
      : [];

    if (isEditMode) {
      const payload = trimSpacesInPayload({
        shortProductName: formData.shortProductName,
        productName: formData.productName,
        productImage: formData.productImage,
        legacyId: formData.legacyId,
        barcodes: barcodesArray,
        transactionId: uuidv4(),
        lastChangeDtm: new Date().toISOString(),
        subAppId: product!.subAppId,
      });
      pendingRef.current = true;
      dispatch(
        updateSmartCoolerProduct({
          id: product!.smartCoolerProductId,
          payload,
        }),
      );
    } else {
      if (!subAppId) return;
      const payload = trimSpacesInPayload({
        shortProductName: formData.shortProductName,
        productName: formData.productName,
        productImage: formData.productImage,
        legacyId: formData.legacyId,
        barcodes: barcodesArray,
        smartCoolerProductId: formData.smartCoolerProductId,
        subAppId,
        transactionId: uuidv4(),
        lastChangeDtm: new Date().toISOString(),
      });
      pendingRef.current = true;
      dispatch(createSmartCoolerProduct(payload));
    }
  };

  const handleClose = () => {
    if (!isLoading) onClose(false);
  };

  // ─── JSX ─────────────────────────────────────────────────────────────

  return (
    <Modal open={isOpen} onClose={handleClose} size="4xl">
      <Modal.Header
        title={
          isEditMode
            ? CREATE_SMART_COOLER_PRODUCTS_PROPS.MODAL.EDIT_TITLE
            : CREATE_SMART_COOLER_PRODUCTS_PROPS.MODAL.CREATE_TITLE
        }
        subtitle={
          isEditMode
            ? CREATE_SMART_COOLER_PRODUCTS_PROPS.MODAL.EDIT_SUBTITLE
            : CREATE_SMART_COOLER_PRODUCTS_PROPS.MODAL.CREATE_SUBTITLE
        }
        icon={<LuPackage className="w-4 h-4" />}
      />

      <form onSubmit={handleSubmit(onSubmit)}>
        <Modal.Body className="flex flex-col gap-3 max-h-[60vh] overflow-y-auto">
          {/* Sub-Application — CREATE mode only */}
          {!isEditMode && (
            <ConfigDropdown
              dataTestId={tid(TID.MODAL_SUBAPP_SELECT)}
              label={
                <span className="flex items-center gap-1">
                  {CREATE_SCP_FORM_PROPS.SUB_APPLICATION.LABEL}
                  <Text as="span" size="base" color="danger" className="ml-0.5">
                    *
                  </Text>
                </span>
              }
              placeholder={CREATE_SCP_FORM_PROPS.SUB_APPLICATION.PLACEHOLDER}
              value={subAppId}
              dynamicLists={subapplications}
              handleSearch={setSearchApplication}
              onDynamicSelect={handleSelectedSubApplication}
              error={isSubmitted && !isSubAppSelected}
              errorMessage={
                isSubmitted && !isSubAppSelected
                  ? CREATE_SCP_FORM_PROPS.SUB_APPLICATION.IS_REQUIRED
                  : undefined
              }
              enableSearch
              icon={LuAppWindow}
            />
          )}

          {/* Short Product Name */}
          <ConfigInput
            dataTestId={tid(TID.MODAL_SHORT_NAME_INPUT)}
            label={
              <FieldLabel
                text={CREATE_SCP_FORM_PROPS.SHORT_PRODUCT_NAME.LABEL}
                info={CREATE_SCP_FORM_PROPS.SHORT_PRODUCT_NAME.INFO}
                required
              />
            }
            placeholder={CREATE_SCP_FORM_PROPS.SHORT_PRODUCT_NAME.PLACEHOLDER}
            icon={LuPackage}
            register={register}
            name={
              CREATE_SCP_FORM_PROPS.SHORT_PRODUCT_NAME.REACT_HOOK_FORM_KEY_NAME
            }
            formValidationEnabled
            validationSchema={{
              required:
                CREATE_SCP_FORM_PROPS.SHORT_PRODUCT_NAME.VALIDATION.REQUIRED,
              minLength: {
                value: 3,
                message:
                  CREATE_SCP_FORM_PROPS.SHORT_PRODUCT_NAME.VALIDATION
                    .MIN_LENGTH,
              },
              maxLength: {
                value: 64,
                message:
                  CREATE_SCP_FORM_PROPS.SHORT_PRODUCT_NAME.VALIDATION
                    .MAX_LENGTH,
              },
            }}
            error={!!errors.shortProductName}
            errorMsg={errors.shortProductName?.message}
            onBlur={(e) => {
              e.target.value = e.target.value.trim();
              trigger(
                CREATE_SCP_FORM_PROPS.SHORT_PRODUCT_NAME
                  .REACT_HOOK_FORM_KEY_NAME,
              );
            }}
          />

          {/* Product Name (optional) */}
          <ConfigInput
            dataTestId={tid(TID.MODAL_FULL_NAME_INPUT)}
            label={
              <FieldLabel
                text={CREATE_SCP_FORM_PROPS.FULL_PRODUCT_NAME.LABEL}
                info={CREATE_SCP_FORM_PROPS.FULL_PRODUCT_NAME.INFO}
              />
            }
            placeholder={CREATE_SCP_FORM_PROPS.FULL_PRODUCT_NAME.PLACEHOLDER}
            icon={LuPackage}
            register={register}
            name={
              CREATE_SCP_FORM_PROPS.FULL_PRODUCT_NAME.REACT_HOOK_FORM_KEY_NAME
            }
            formValidationEnabled
            validationSchema={{
              minLength: {
                value: 3,
                message:
                  CREATE_SCP_FORM_PROPS.FULL_PRODUCT_NAME.VALIDATION.MIN_LENGTH,
              },
              maxLength: {
                value: 128,
                message:
                  CREATE_SCP_FORM_PROPS.FULL_PRODUCT_NAME.VALIDATION.MAX_LENGTH,
              },
            }}
            error={!!errors.productName}
            errorMsg={errors.productName?.message}
            onBlur={(e) => {
              e.target.value = e.target.value.trim();
              trigger(
                CREATE_SCP_FORM_PROPS.FULL_PRODUCT_NAME
                  .REACT_HOOK_FORM_KEY_NAME,
              );
            }}
          />

          {/* Smart Product ID + hyphen toggle — CREATE mode only */}
          {!isEditMode && (
            <ConfigInput
              label={
                <span className="flex items-center justify-between w-full gap-1">
                  {/* Left: field name */}
                  <span className="flex items-center gap-1">
                    {CREATE_SCP_FORM_PROPS.SMART_PRODUCT_ID.LABEL}
                    <Text
                      as="span"
                      size="base"
                      color="danger"
                      className="ml-0.5"
                    >
                      *
                    </Text>
                  </span>
                  {/* Right: info tooltip + hyphen toggle */}
                  <span className="flex items-center gap-2">
                    <InfoTooltip
                      info={CREATE_SCP_FORM_PROPS.SMART_PRODUCT_ID.INFO}
                    />
                    <AppButton
                      data-testid={tid(TID.MODAL_HYPHEN_TOGGLE)}
                      variant="unstyled"
                      ripple={false}
                      type="button"
                      onClick={() => {
                        const current = getValues(
                          CREATE_SCP_FORM_PROPS.SMART_PRODUCT_ID
                            .REACT_HOOK_FORM_KEY_NAME,
                        );
                        const next = !isHyphened;
                        setIsHyphened(next);
                        setValue(
                          CREATE_SCP_FORM_PROPS.SMART_PRODUCT_ID
                            .REACT_HOOK_FORM_KEY_NAME,
                          toggleHyphenation(current, next),
                          { shouldValidate: true },
                        );
                      }}
                      className="flex items-center gap-1.5 text-[10px] font-semibold px-2 py-0.5 rounded-full border transition-all duration-150 border-primary/30 bg-primary/5 text-primary hover:bg-primary/10 dark:border-primary/40 dark:bg-primary/10"
                    >
                      <span
                        className={`inline-block w-6 h-3 rounded-full transition-colors duration-200 relative flex-shrink-0 ${isHyphened ? 'bg-primary' : 'bg-gray-300 dark:bg-gray-600'}`}
                      >
                        <span
                          className={`absolute top-0.5 w-2 h-2 bg-white rounded-full shadow transition-transform duration-200 ${isHyphened ? 'translate-x-3 left-0.5' : 'left-0.5 translate-x-0'}`}
                        />
                      </span>
                      {isHyphened
                        ? CREATE_SCP_FORM_PROPS.SMART_PRODUCT_ID.TOGGLE_LABELS
                          .HYPHENED_36_CHARS
                        : CREATE_SCP_FORM_PROPS.SMART_PRODUCT_ID.TOGGLE_LABELS
                          .NON_HYPHENED_32_CHARS}
                    </AppButton>
                  </span>
                </span>
              }
              placeholder={CREATE_SCP_FORM_PROPS.SMART_PRODUCT_ID.PLACEHOLDER}
              dataTestId={tid(TID.MODAL_SMART_PRODUCT_ID_INPUT)}
              icon={LuScan}
              register={register}
              name={
                CREATE_SCP_FORM_PROPS.SMART_PRODUCT_ID.REACT_HOOK_FORM_KEY_NAME
              }
              formValidationEnabled
              validationSchema={{
                required:
                  CREATE_SCP_FORM_PROPS.SMART_PRODUCT_ID.VALIDATION.REQUIRED,
                minLength: {
                  value: 32,
                  message:
                    CREATE_SCP_FORM_PROPS.SMART_PRODUCT_ID.VALIDATION
                      .LENGTH_ERROR,
                },
                maxLength: {
                  value: 36,
                  message:
                    CREATE_SCP_FORM_PROPS.SMART_PRODUCT_ID.VALIDATION
                      .LENGTH_ERROR,
                },
              }}
              value={smartProductIdValue}
              error={!!errors.smartCoolerProductId}
              errorMsg={errors.smartCoolerProductId?.message}
              onBlur={(e) => {
                e.target.value = e.target.value.trim();
                trigger(
                  CREATE_SCP_FORM_PROPS.SMART_PRODUCT_ID
                    .REACT_HOOK_FORM_KEY_NAME,
                );
              }}
            />
          )}

          {/* Legacy ID (optional) */}
          <ConfigInput
            dataTestId={tid(TID.MODAL_LEGACY_ID_INPUT)}
            label={
              <FieldLabel
                text={CREATE_SCP_FORM_PROPS.LEGACY_ID.LABEL}
                info={CREATE_SCP_FORM_PROPS.LEGACY_ID.INFO}
              />
            }
            placeholder={CREATE_SCP_FORM_PROPS.LEGACY_ID.PLACEHOLDER}
            icon={LuHash}
            register={register}
            name={CREATE_SCP_FORM_PROPS.LEGACY_ID.REACT_HOOK_FORM_KEY_NAME}
            formValidationEnabled
            validationSchema={{
              maxLength: {
                value: 32,
                message: CREATE_SCP_FORM_PROPS.LEGACY_ID.VALIDATION.MAX_LENGTH,
              },
              validate: (v: string) => {
                if (v && /^\d+$/.test(v)) {
                  return CREATE_SCP_FORM_PROPS.LEGACY_ID.VALIDATION
                    .INVALID_FORMAT;
                }
                return true;
              },
            }}
            error={!!errors.legacyId}
            errorMsg={errors.legacyId?.message}
            onBlur={(e) => {
              e.target.value = e.target.value.trim();
              trigger(CREATE_SCP_FORM_PROPS.LEGACY_ID.REACT_HOOK_FORM_KEY_NAME);
            }}
          />

          {/* Barcodes */}
          <ConfigInput
            dataTestId={tid(TID.MODAL_BARCODES_INPUT)}
            label={
              <FieldLabel
                text={CREATE_SCP_FORM_PROPS.BARCODES.LABEL}
                info={CREATE_SCP_FORM_PROPS.BARCODES.INFO}
                required
              />
            }
            placeholder={CREATE_SCP_FORM_PROPS.BARCODES.PLACEHOLDER}
            icon={LuScanLine}
            register={register}
            name={CREATE_SCP_FORM_PROPS.BARCODES.REACT_HOOK_FORM_KEY_NAME}
            formValidationEnabled
            validationSchema={checkBarcodeValidationRule}
            error={!!errors.barcodes}
            errorMsg={errors.barcodes?.message}
            onBlur={() =>
              trigger(CREATE_SCP_FORM_PROPS.BARCODES.REACT_HOOK_FORM_KEY_NAME)
            }
          />

          {/* Product Image URL (optional) */}
          <ConfigInput
            dataTestId={tid(TID.MODAL_IMAGE_INPUT)}
            label={
              <FieldLabel
                text={CREATE_SCP_FORM_PROPS.PRODUCT_IMAGE.LABEL}
                info={CREATE_SCP_FORM_PROPS.PRODUCT_IMAGE.INFO}
              />
            }
            placeholder={CREATE_SCP_FORM_PROPS.PRODUCT_IMAGE.PLACEHOLDER}
            icon={LuLink}
            register={register}
            name={CREATE_SCP_FORM_PROPS.PRODUCT_IMAGE.REACT_HOOK_FORM_KEY_NAME}
            formValidationEnabled
            validationSchema={{
              pattern: {
                value: CREATE_SCP_FORM_PROPS.PRODUCT_IMAGE.VALIDATION.PATTERN,
                message:
                  CREATE_SCP_FORM_PROPS.PRODUCT_IMAGE.VALIDATION.INVALID_URL,
              },
              maxLength: {
                value: 1000,
                message:
                  CREATE_SCP_FORM_PROPS.PRODUCT_IMAGE.VALIDATION.MAX_LENGTH_MSG,
              },
            }}
            error={!!errors.productImage}
            errorMsg={errors.productImage?.message}
            onBlur={() =>
              trigger(
                CREATE_SCP_FORM_PROPS.PRODUCT_IMAGE.REACT_HOOK_FORM_KEY_NAME,
              )
            }
          />
        </Modal.Body>

        <Modal.Footer>
          <Modal.CancelButton
            data-testid={tid(TID.MODAL_CANCEL_BTN)}
            onClick={handleClose}
            disabled={isLoading}
          >
            Cancel
          </Modal.CancelButton>
          <Modal.ConfirmButton
            data-testid={tid(TID.MODAL_SAVE_BTN)}
            type="submit"
            disabled={isSaveDisabled}
            loading={isLoading}
            icon={
              isEditMode ? (
                <LuPencil className="w-4 h-4" />
              ) : (
                <LuPlus className="w-4 h-4" />
              )
            }
          >
            {isEditMode ? 'Save Changes' : 'Create'}
          </Modal.ConfirmButton>
        </Modal.Footer>
      </form>
    </Modal>
  );
};

export default CrudCoolerProductModal;
